import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, FilterIcon, BuildingIcon, HomeIcon, DollarSignIcon, CalendarIcon, ThumbsUpIcon, ThumbsDownIcon, FileTextIcon, HomeIcon as HomeIconSolid, KeyIcon, AlertTriangleIcon, ArrowRightIcon, PlusIcon, TagIcon } from 'lucide-react';
import Button from './common/Button';
// Deal status types (matching those in DealInput.tsx)
const DEAL_STATUS = {
  ANALYSIS: 'analysis',
  PASSED: 'passed',
  OFFER: 'offer',
  ESCROW: 'escrow',
  PORTFOLIO: 'portfolio'
};
// Mock data for deal history
const mockDealHistory = [{
  id: 'deal1',
  address: '123 Main Street, Anytown, USA',
  dateAdded: '2023-05-15',
  status: DEAL_STATUS.PORTFOLIO,
  purchasePrice: 320000,
  arv: 420000,
  constructionCost: 85000,
  roi: 17.6,
  notes: 'Completed full renovation with modern finishes',
  imageUrl: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  category: 'Single Family'
}, {
  id: 'deal2',
  address: '456 Oak Avenue, Anytown, USA',
  dateAdded: '2023-06-20',
  status: DEAL_STATUS.ESCROW,
  purchasePrice: 275000,
  arv: 380000,
  constructionCost: 45000,
  roi: 13.3,
  notes: 'Kitchen and bath update in progress',
  imageUrl: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  category: 'Single Family'
}, {
  id: 'deal3',
  address: '789 Pine Street, Anytown, USA',
  dateAdded: '2023-07-05',
  status: DEAL_STATUS.OFFER,
  purchasePrice: 450000,
  arv: 650000,
  constructionCost: 120000,
  roi: 33.3,
  notes: 'Offer accepted, planning to add ADU in backyard',
  imageUrl: 'https://images.unsplash.com/photo-1599427303058-f04cbcf4756f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  category: 'Single Family'
}, {
  id: 'deal4',
  address: '101 Maple Drive, Anytown, USA',
  dateAdded: '2023-07-10',
  status: DEAL_STATUS.ANALYSIS,
  purchasePrice: 380000,
  arv: 480000,
  constructionCost: 60000,
  roi: 15.8,
  notes: 'Currently analyzing renovation options',
  imageUrl: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  category: 'Single Family'
}, {
  id: 'deal5',
  address: '202 Elm Street, Anytown, USA',
  dateAdded: '2023-06-10',
  status: DEAL_STATUS.PASSED,
  purchasePrice: 520000,
  arv: 590000,
  constructionCost: 95000,
  roi: 6.5,
  notes: 'Passed due to low ROI and high renovation costs',
  imageUrl: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  category: 'Single Family'
}, {
  id: 'deal6',
  address: '303 Cedar Lane, Anytown, USA',
  dateAdded: '2023-04-22',
  status: DEAL_STATUS.PORTFOLIO,
  purchasePrice: 850000,
  arv: 1200000,
  constructionCost: 150000,
  roi: 22.4,
  notes: 'Multi-unit property, fully renovated and rented',
  imageUrl: 'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  category: 'Multi-Family'
}, {
  id: 'deal7',
  address: '404 Birch Court, Anytown, USA',
  dateAdded: '2023-05-30',
  status: DEAL_STATUS.PASSED,
  purchasePrice: 620000,
  arv: 680000,
  constructionCost: 110000,
  roi: 3.8,
  notes: 'Passed due to structural issues discovered during inspection',
  imageUrl: 'https://images.unsplash.com/photo-1605146769289-440113cc3d00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  category: 'Single Family'
}, {
  id: 'deal8',
  address: '505 Walnut Avenue, Anytown, USA',
  dateAdded: '2023-07-01',
  status: DEAL_STATUS.ANALYSIS,
  purchasePrice: 410000,
  arv: 550000,
  constructionCost: 70000,
  roi: 19.2,
  notes: 'Potential commercial conversion, analyzing zoning requirements',
  imageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  category: 'Commercial'
}];
// Categories for property types
const propertyCategories = ['All', 'Single Family', 'Multi-Family', 'Commercial', 'Industrial', 'Land'];
const DealHistory = () => {
  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortBy, setSortBy] = useState('dateAdded');
  const [sortOrder, setSortOrder] = useState('desc');
  // State for deal history
  const [dealHistory, setDealHistory] = useState([]);
  // Load deal history from localStorage on component mount
  useEffect(() => {
    const savedDeals = localStorage.getItem('dealHistory');
    if (savedDeals) {
      setDealHistory(JSON.parse(savedDeals));
    } else {
      // Use mock data if no saved deals
      setDealHistory(mockDealHistory);
      localStorage.setItem('dealHistory', JSON.stringify(mockDealHistory));
    }
  }, []);
  // Filter deals based on search, status, and category
  const filteredDeals = dealHistory.filter(deal => {
    const matchesSearch = deal.address.toLowerCase().includes(searchTerm.toLowerCase()) || deal.notes && deal.notes.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || deal.status === statusFilter;
    const matchesCategory = categoryFilter === 'All' || deal.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });
  // Sort filtered deals
  const sortedDeals = [...filteredDeals].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'dateAdded') {
      comparison = new Date(a.dateAdded) - new Date(b.dateAdded);
    } else if (sortBy === 'purchasePrice') {
      comparison = a.purchasePrice - b.purchasePrice;
    } else if (sortBy === 'roi') {
      comparison = a.roi - b.roi;
    } else if (sortBy === 'address') {
      comparison = a.address.localeCompare(b.address);
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });
  // Group deals by status for the dashboard view
  const dealsByStatus = {
    [DEAL_STATUS.ANALYSIS]: sortedDeals.filter(deal => deal.status === DEAL_STATUS.ANALYSIS),
    [DEAL_STATUS.PASSED]: sortedDeals.filter(deal => deal.status === DEAL_STATUS.PASSED),
    [DEAL_STATUS.OFFER]: sortedDeals.filter(deal => deal.status === DEAL_STATUS.OFFER),
    [DEAL_STATUS.ESCROW]: sortedDeals.filter(deal => deal.status === DEAL_STATUS.ESCROW),
    [DEAL_STATUS.PORTFOLIO]: sortedDeals.filter(deal => deal.status === DEAL_STATUS.PORTFOLIO)
  };
  // Helper function to get status badge color
  const getStatusBadgeColor = status => {
    switch (status) {
      case DEAL_STATUS.ANALYSIS:
        return 'bg-blue-100 text-blue-800';
      case DEAL_STATUS.PASSED:
        return 'bg-red-100 text-red-800';
      case DEAL_STATUS.OFFER:
        return 'bg-yellow-100 text-yellow-800';
      case DEAL_STATUS.ESCROW:
        return 'bg-purple-100 text-purple-800';
      case DEAL_STATUS.PORTFOLIO:
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Helper function to get status text
  const getStatusText = status => {
    switch (status) {
      case DEAL_STATUS.ANALYSIS:
        return 'Under Analysis';
      case DEAL_STATUS.PASSED:
        return 'Passed On';
      case DEAL_STATUS.OFFER:
        return 'Offer Written';
      case DEAL_STATUS.ESCROW:
        return 'In Escrow';
      case DEAL_STATUS.PORTFOLIO:
        return 'In Portfolio';
      default:
        return 'Unknown';
    }
  };
  // Helper function to get status icon
  const getStatusIcon = status => {
    switch (status) {
      case DEAL_STATUS.ANALYSIS:
        return <AlertTriangleIcon className="w-4 h-4 mr-1" data-id="element-134" />;
      case DEAL_STATUS.PASSED:
        return <ThumbsDownIcon className="w-4 h-4 mr-1" data-id="element-135" />;
      case DEAL_STATUS.OFFER:
        return <FileTextIcon className="w-4 h-4 mr-1" data-id="element-136" />;
      case DEAL_STATUS.ESCROW:
        return <KeyIcon className="w-4 h-4 mr-1" data-id="element-137" />;
      case DEAL_STATUS.PORTFOLIO:
        return <HomeIconSolid className="w-4 h-4 mr-1" data-id="element-138" />;
      default:
        return <AlertTriangleIcon className="w-4 h-4 mr-1" data-id="element-139" />;
    }
  };
  return <div className="max-w-7xl mx-auto" data-id="element-140">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-141">Deal History</h1>
      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6" data-id="element-142">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4" data-id="element-143">
          <div className="relative flex-grow max-w-md" data-id="element-144">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-145">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-146" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search properties..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-147" />
          </div>
          <Link to="/deal-input" data-id="element-148">
            <Button data-id="element-149">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-150" />
              Analyze New Deal
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-151">
          <div data-id="element-152">
            <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-153">
              Status
            </label>
            <select className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-154">
              <option value="all" data-id="element-155">All Statuses</option>
              <option value={DEAL_STATUS.ANALYSIS} data-id="element-156">Under Analysis</option>
              <option value={DEAL_STATUS.OFFER} data-id="element-157">Offer Written</option>
              <option value={DEAL_STATUS.ESCROW} data-id="element-158">In Escrow</option>
              <option value={DEAL_STATUS.PORTFOLIO} data-id="element-159">In Portfolio</option>
              <option value={DEAL_STATUS.PASSED} data-id="element-160">Passed On</option>
            </select>
          </div>
          <div data-id="element-161">
            <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-162">
              Property Type
            </label>
            <select className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-163">
              {propertyCategories.map(category => <option key={category} value={category} data-id="element-164">
                  {category}
                </option>)}
            </select>
          </div>
          <div data-id="element-165">
            <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-166">
              Sort By
            </label>
            <select className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={sortBy} onChange={e => setSortBy(e.target.value)} data-id="element-167">
              <option value="dateAdded" data-id="element-168">Date Added</option>
              <option value="purchasePrice" data-id="element-169">Purchase Price</option>
              <option value="roi" data-id="element-170">ROI</option>
              <option value="address" data-id="element-171">Address</option>
            </select>
          </div>
          <div data-id="element-172">
            <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-173">
              Sort Order
            </label>
            <select className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={sortOrder} onChange={e => setSortOrder(e.target.value)} data-id="element-174">
              <option value="desc" data-id="element-175">Descending</option>
              <option value="asc" data-id="element-176">Ascending</option>
            </select>
          </div>
        </div>
      </div>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-id="element-177">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-178">
          <div className="flex items-center" data-id="element-179">
            <div className="p-2 bg-blue-100 rounded-full" data-id="element-180">
              <AlertTriangleIcon className="h-5 w-5 text-blue-600" data-id="element-181" />
            </div>
            <div className="ml-3" data-id="element-182">
              <p className="text-xs font-medium text-gray-500" data-id="element-183">
                Under Analysis
              </p>
              <p className="text-xl font-semibold text-gray-900" data-id="element-184">
                {dealsByStatus[DEAL_STATUS.ANALYSIS].length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-185">
          <div className="flex items-center" data-id="element-186">
            <div className="p-2 bg-yellow-100 rounded-full" data-id="element-187">
              <FileTextIcon className="h-5 w-5 text-yellow-600" data-id="element-188" />
            </div>
            <div className="ml-3" data-id="element-189">
              <p className="text-xs font-medium text-gray-500" data-id="element-190">Offer Written</p>
              <p className="text-xl font-semibold text-gray-900" data-id="element-191">
                {dealsByStatus[DEAL_STATUS.OFFER].length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-192">
          <div className="flex items-center" data-id="element-193">
            <div className="p-2 bg-purple-100 rounded-full" data-id="element-194">
              <KeyIcon className="h-5 w-5 text-purple-600" data-id="element-195" />
            </div>
            <div className="ml-3" data-id="element-196">
              <p className="text-xs font-medium text-gray-500" data-id="element-197">In Escrow</p>
              <p className="text-xl font-semibold text-gray-900" data-id="element-198">
                {dealsByStatus[DEAL_STATUS.ESCROW].length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-199">
          <div className="flex items-center" data-id="element-200">
            <div className="p-2 bg-green-100 rounded-full" data-id="element-201">
              <HomeIconSolid className="h-5 w-5 text-green-600" data-id="element-202" />
            </div>
            <div className="ml-3" data-id="element-203">
              <p className="text-xs font-medium text-gray-500" data-id="element-204">In Portfolio</p>
              <p className="text-xl font-semibold text-gray-900" data-id="element-205">
                {dealsByStatus[DEAL_STATUS.PORTFOLIO].length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-206">
          <div className="flex items-center" data-id="element-207">
            <div className="p-2 bg-red-100 rounded-full" data-id="element-208">
              <ThumbsDownIcon className="h-5 w-5 text-red-600" data-id="element-209" />
            </div>
            <div className="ml-3" data-id="element-210">
              <p className="text-xs font-medium text-gray-500" data-id="element-211">Passed On</p>
              <p className="text-xl font-semibold text-gray-900" data-id="element-212">
                {dealsByStatus[DEAL_STATUS.PASSED].length}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Deal List */}
      {sortedDeals.length > 0 ? <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-213">
          <div className="overflow-x-auto" data-id="element-214">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-215">
              <thead className="bg-gray-50" data-id="element-216">
                <tr data-id="element-217">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-218">
                    Property
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-219">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-220">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-221">
                    Purchase Price
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-222">
                    ARV
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-223">
                    ROI
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-224">
                    Date Added
                  </th>
                  <th scope="col" className="relative px-6 py-3" data-id="element-225">
                    <span className="sr-only" data-id="element-226">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-227">
                {sortedDeals.map(deal => <tr key={deal.id} className="hover:bg-gray-50" data-id="element-228">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-229">
                      <div className="flex items-center" data-id="element-230">
                        <div className="flex-shrink-0 h-10 w-10" data-id="element-231">
                          <img className="h-10 w-10 rounded-md object-cover" src={deal.imageUrl} alt="" data-id="element-232" />
                        </div>
                        <div className="ml-4" data-id="element-233">
                          <div className="text-sm font-medium text-gray-900" data-id="element-234">
                            {deal.address}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-235">
                            {deal.notes}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-236">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(deal.status)}`} data-id="element-237">
                        {getStatusIcon(deal.status)}
                        {getStatusText(deal.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-238">
                      {deal.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-239">
                      ${deal.purchasePrice.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-240">
                      ${deal.arv.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-241">
                      <span className={`text-sm font-medium ${deal.roi >= 15 ? 'text-green-600' : deal.roi >= 10 ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-242">
                        {deal.roi.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-243">
                      {deal.dateAdded}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-244">
                      <Link to={`/deal-input?id=${deal.id}`} className="text-blue-600 hover:text-blue-900" data-id="element-245">
                        View
                      </Link>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div> : <div className="bg-white border border-gray-200 rounded-lg p-8 text-center" data-id="element-246">
          <DollarSignIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-247" />
          <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-248">
            No deals found
          </h3>
          <p className="text-gray-500 mb-4" data-id="element-249">
            {searchTerm || statusFilter !== 'all' || categoryFilter !== 'All' ? 'Try adjusting your filters to see more results' : 'Start by analyzing your first property'}
          </p>
          <Link to="/deal-input" data-id="element-250">
            <Button data-id="element-251">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-252" />
              Analyze New Deal
            </Button>
          </Link>
        </div>}
      {/* Deal Categories Section */}
      {sortedDeals.length > 0 && <div className="mt-8" data-id="element-253">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-254">
            Deal Categories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-255">
            {/* Active Deals */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-256">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-257">
                <TagIcon className="w-5 h-5 mr-2 text-blue-500" data-id="element-258" />
                Active Deals
              </h3>
              <div className="space-y-4" data-id="element-259">
                {[...dealsByStatus[DEAL_STATUS.ANALYSIS], ...dealsByStatus[DEAL_STATUS.OFFER], ...dealsByStatus[DEAL_STATUS.ESCROW]].slice(0, 3).map(deal => <div key={deal.id} className="flex items-start border-b border-gray-100 pb-3" data-id="element-260">
                      <img src={deal.imageUrl} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" data-id="element-261" />
                      <div className="ml-3 flex-1 min-w-0" data-id="element-262">
                        <div className="flex justify-between" data-id="element-263">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-264">
                            {deal.address}
                          </p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(deal.status)}`} data-id="element-265">
                            {getStatusText(deal.status)}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center text-xs text-gray-500" data-id="element-266">
                          <DollarSignIcon className="w-3.5 h-3.5 mr-1" data-id="element-267" />
                          <span data-id="element-268">${deal.purchasePrice.toLocaleString()}</span>
                          <span className="mx-1" data-id="element-269">•</span>
                          <span data-id="element-270">ROI: {deal.roi.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>)}
                {[...dealsByStatus[DEAL_STATUS.ANALYSIS], ...dealsByStatus[DEAL_STATUS.OFFER], ...dealsByStatus[DEAL_STATUS.ESCROW]].length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-271">
                    No active deals
                  </p>}
                {[...dealsByStatus[DEAL_STATUS.ANALYSIS], ...dealsByStatus[DEAL_STATUS.OFFER], ...dealsByStatus[DEAL_STATUS.ESCROW]].length > 3 && <div className="text-center mt-2" data-id="element-272">
                    <button onClick={() => {
                setStatusFilter('all');
                setCategoryFilter('All');
                setSearchTerm('');
              }} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-273">
                      View all active deals
                    </button>
                  </div>}
              </div>
            </div>
            {/* Completed Deals */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-274">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-275">
                <HomeIconSolid className="w-5 h-5 mr-2 text-green-500" data-id="element-276" />
                Completed Deals
              </h3>
              <div className="space-y-4" data-id="element-277">
                {dealsByStatus[DEAL_STATUS.PORTFOLIO].slice(0, 3).map(deal => <div key={deal.id} className="flex items-start border-b border-gray-100 pb-3" data-id="element-278">
                      <img src={deal.imageUrl} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" data-id="element-279" />
                      <div className="ml-3 flex-1 min-w-0" data-id="element-280">
                        <div className="flex justify-between" data-id="element-281">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-282">
                            {deal.address}
                          </p>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-283">
                            In Portfolio
                          </span>
                        </div>
                        <div className="mt-1 flex items-center text-xs text-gray-500" data-id="element-284">
                          <DollarSignIcon className="w-3.5 h-3.5 mr-1" data-id="element-285" />
                          <span data-id="element-286">${deal.purchasePrice.toLocaleString()}</span>
                          <span className="mx-1" data-id="element-287">•</span>
                          <span data-id="element-288">ROI: {deal.roi.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>)}
                {dealsByStatus[DEAL_STATUS.PORTFOLIO].length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-289">
                    No completed deals
                  </p>}
                {dealsByStatus[DEAL_STATUS.PORTFOLIO].length > 3 && <div className="text-center mt-2" data-id="element-290">
                    <button onClick={() => {
                setStatusFilter(DEAL_STATUS.PORTFOLIO);
                setCategoryFilter('All');
                setSearchTerm('');
              }} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-291">
                      View all completed deals
                    </button>
                  </div>}
              </div>
            </div>
          </div>
          {/* Passed Deals */}
          <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-292">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-293">
              <ThumbsDownIcon className="w-5 h-5 mr-2 text-red-500" data-id="element-294" />
              Passed Deals
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-295">
              {dealsByStatus[DEAL_STATUS.PASSED].slice(0, 3).map(deal => <div key={deal.id} className="border border-gray-200 rounded-lg p-4 hover:border-red-200 hover:bg-red-50 transition-colors" data-id="element-296">
                  <div className="flex justify-between items-start mb-2" data-id="element-297">
                    <h4 className="font-medium text-gray-900 truncate" data-id="element-298">
                      {deal.address}
                    </h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-299">
                      Passed
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2" data-id="element-300">
                    {deal.notes}
                  </p>
                  <div className="text-sm" data-id="element-301">
                    <div className="flex justify-between mb-1" data-id="element-302">
                      <span className="text-gray-500" data-id="element-303">Price:</span>
                      <span className="font-medium" data-id="element-304">
                        ${deal.purchasePrice.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between mb-1" data-id="element-305">
                      <span className="text-gray-500" data-id="element-306">ARV:</span>
                      <span className="font-medium" data-id="element-307">
                        ${deal.arv.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-308">
                      <span className="text-gray-500" data-id="element-309">ROI:</span>
                      <span className="font-medium text-red-600" data-id="element-310">
                        {deal.roi.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>)}
              {dealsByStatus[DEAL_STATUS.PASSED].length === 0 && <div className="col-span-3 text-center py-4" data-id="element-311">
                  <p className="text-sm text-gray-500 italic" data-id="element-312">
                    No passed deals
                  </p>
                </div>}
            </div>
            {dealsByStatus[DEAL_STATUS.PASSED].length > 3 && <div className="text-center mt-4" data-id="element-313">
                <button onClick={() => {
            setStatusFilter(DEAL_STATUS.PASSED);
            setCategoryFilter('All');
            setSearchTerm('');
          }} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-314">
                  View all passed deals
                </button>
              </div>}
          </div>
        </div>}
    </div>;
};
export default DealHistory;